import { Stack, Spinner, SpinnerSize, MessageBar, MessageBarType, DatePicker, IDatePickerStrings, defaultDatePickerStrings } from "@fluentui/react";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box, Typography, ToggleButtonGroup, ToggleButton, Tooltip, LinearProgress, Fade, Popover, Button } from "@mui/material";
import { CalendarLtr24Regular, History24Regular, CalendarAgenda24Regular, ArrowClockwise16Regular, CalendarEdit24Regular } from "@fluentui/react-icons";
import { useGetOverviewQuery } from "../../../slices/CloseTheLoop.slice";
import { logPageView } from "../../../helper/AppInsightsHelper";
import { HierarchicalProductFilter } from "./HierarchicalProductFilter";
import {
  OverviewContext,
  OverviewContextType,
  DateRangeOption,
  CustomDateRange,
  THEME,
  TopSection,
  MonthlyInsights,
  TopThemes,
  BreakdownCharts,
  CombinedDonutCharts,
  SLAPerformance
} from "../Sections";

const Overview: React.FC = () => {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeOption>("2years");
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>({ startDate: null, endDate: null });
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  // Brief visual "refreshing" pulse for client-side switches (date range) that don't hit the API
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  const switchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerRefreshPulse = useCallback(() => {
    if (switchTimerRef.current) {
      clearTimeout(switchTimerRef.current);
    }
    setIsSwitching(true);
    switchTimerRef.current = setTimeout(() => setIsSwitching(false), 500);
  }, []);

  useEffect(() => {
    return () => {
      if (switchTimerRef.current) {
        clearTimeout(switchTimerRef.current);
      }
    };
  }, []);

  // Build query params - include custom date range if selected
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (selectedProducts.length > 0) {
      params.products = selectedProducts.join(",");
    }
    // Add custom date range to API call when custom is selected
    if (dateRange === "custom" && customDateRange.startDate) {
      params.startDate = customDateRange.startDate.toISOString().split("T")[0];
      params.endDate = (customDateRange.endDate || new Date()).toISOString().split("T")[0];
    }
    return params;
  }, [selectedProducts, dateRange, customDateRange]);

  // API query - returns ALL data + pre-calculated 2-year data, client switches based on dateRange
  const { data, isLoading, error, isFetching } = useGetOverviewQuery(
    queryParams,
    {
      // Refetch when args change
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false
    }
  );

  // Use pre-calculated summary from API (no complex client-side calculation needed)
  const filteredSummary = useMemo(() => {
    if (!data) return undefined;
    // API returns both summary (all data) and summary2Years (2-year filtered)
    // For custom range, API will return filtered data in main summary
    if (dateRange === "custom") return data.summary;
    return dateRange === "2years" && data.summary2Years ? data.summary2Years : data.summary;
  }, [data, dateRange]);

  // Calculate total years of data from monthly response
  const totalDataYears = useMemo(() => {
    if (!data?.monthlyRequestCounts?.length) return 0;
    const years = new Set(data.monthlyRequestCounts.map((m) => m.year));
    return years.size;
  }, [data?.monthlyRequestCounts]);

  const handleDateRangeChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newRange: DateRangeOption | null) => {
      // Allow clicking "Custom" again to re-open the date picker (to modify/clear)
      if (newRange === "custom" || (newRange === null && dateRange === "custom")) {
        // Open the date picker popover
        setDatePickerAnchor(event.currentTarget as HTMLElement);
        return;
      }

      if (newRange !== null && newRange !== dateRange) {
        setDateRange(newRange);
        triggerRefreshPulse();
      }
    },
    [dateRange, triggerRefreshPulse]
  );

  // Handle custom date picker
  const handleCustomDateApply = useCallback(() => {
    if (customDateRange.startDate) {
      const resolvedEndDate = customDateRange.endDate || new Date();
      setCustomDateRange((prev: CustomDateRange) => ({
        ...prev,
        endDate: resolvedEndDate
      }));
      setDateRange("custom");
      setDatePickerAnchor(null);
      triggerRefreshPulse();
    }
  }, [customDateRange, triggerRefreshPulse]);

  // Handle clearing custom date range
  const handleCustomDateClear = useCallback(() => {
    setCustomDateRange({ startDate: null, endDate: null });
    setDateRange("2years");
    setDatePickerAnchor(null);
    triggerRefreshPulse();
  }, [triggerRefreshPulse]);

  const handleDatePickerClose = useCallback(() => {
    setDatePickerAnchor(null);
  }, []);

  // Format date range label for display
  const customDateLabel = useMemo(() => {
    if (customDateRange.startDate && customDateRange.endDate) {
      const formatDate = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      return `${formatDate(customDateRange.startDate)} - ${formatDate(customDateRange.endDate)}`;
    }
    return "Custom";
  }, [customDateRange]);

  useEffect(() => {
    logPageView("Close The Loop - Overview Tab", window.location.href);
  }, []);

  // Get product hierarchy from API
  const productHierarchy = useMemo(() => data?.productHierarchy ?? {}, [data?.productHierarchy]);

  // Handle product filter change
  const handleProductFilterChange = useCallback((products: string[]) => {
    setSelectedProducts(products);
    triggerRefreshPulse();
  }, [triggerRefreshPulse]);

  // Create filtered data for context - use pre-calculated 2-year data from API or custom filtered
  const filteredData = useMemo(() => {
    if (!data) return undefined;

    // For "all" or "custom" (API returns filtered data for custom), use data as-is
    if (dateRange === "all" || dateRange === "custom") return data;

    // Helper to fallback to all-time data if 2-year data is empty
    const useWithFallback = <T,>(twoYearData: T[] | undefined, allTimeData: T[]): T[] =>
      (twoYearData && twoYearData.length > 0) ? twoYearData : allTimeData;

    // For 2 years, use pre-calculated data from API (with fallback if empty)
    return {
      ...data,
      summary: data.summary2Years ?? data.summary,
      biggestGap: data.biggestGap2Years ?? data.biggestGap,
      fastest: data.fastest2Years ?? data.fastest,
      pipelineSnapshot: useWithFallback(data.pipelineSnapshot2Years, data.pipelineSnapshot),
      internalStatusSnapshot: useWithFallback(data.internalStatusSnapshot2Years, data.internalStatusSnapshot),
      sourceBreakdown: useWithFallback(data.sourceBreakdown2Years, data.sourceBreakdown),
      geographyBreakdown: useWithFallback(data.geographyBreakdown2Years, data.geographyBreakdown),
      priorityBreakdown: useWithFallback(data.priorityBreakdown2Years, data.priorityBreakdown),
      slaPerformance: useWithFallback(data.slaPerformance2Years, data.slaPerformance),
      overallSLAPercentage: data.overallSLAPercentage2Years ?? data.overallSLAPercentage,
      stagesWithinSLA: data.stagesWithinSLA2Years ?? data.stagesWithinSLA,
      totalStages: data.totalStages2Years ?? data.totalStages,
    };
  }, [data, dateRange]);

  const contextValue: OverviewContextType = {
    data: filteredData,
    isLoading: isLoading,
    selectedProducts,
    dateRange,
    setDateRange,
    customDateRange,
    setCustomDateRange,
    filteredMonthlyRequestCounts: data?.monthlyRequestCounts ?? [],
    filteredMonthlyAvgEndToEndDays: data?.monthlyAvgEndToEndDays ?? [],
    totalDataYears
  };

  // Combined refresh indicator: API refetch (product filter) OR client-side switch (date range)
  const isRefreshing = (isFetching || isSwitching) && !!data;

  if (isLoading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
          gap: 2
        }}
      >
        <Spinner size={SpinnerSize.large} label="Loading overview data..." />
      </Box>
    );
  }

  if (error) {
    return (
      <MessageBar messageBarType={MessageBarType.error}>
        Failed to load overview data. Please try again later.
      </MessageBar>
    );
  }

  return (
    <OverviewContext.Provider value={contextValue}>
      {/* Keyframes for the refresh spinner icon */}
      <style>{`@keyframes ctl-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {/* Top progress bar shown whenever data is refreshing (filter/date change) */}
      <Fade in={isRefreshing} unmountOnExit>
        <LinearProgress
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            height: 3,
            borderRadius: "3px",
            mb: 1,
            backgroundColor: "rgba(0,120,212,0.12)",
            "& .MuiLinearProgress-bar": { backgroundColor: THEME.primary }
          }}
        />
      </Fade>
      <Stack tokens={{ childrenGap: 20 }} style={{ overflow: "hidden", position: "relative" }}>
        {/* Filters Row - Product Filter & Date Range Toggle */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            px: 2,
            py: 1.5,
            backgroundColor: "#FAFBFC",
            borderRadius: "12px",
            border: "1px solid #E1E5EB"
          }}
        >
          {/* Left side - Product Filter (Hierarchical: ProductFamily > Products) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {/* Hierarchical Product Filter */}
            <HierarchicalProductFilter
              productHierarchy={productHierarchy}
              selectedProducts={selectedProducts}
              onSelectionChange={handleProductFilterChange}
            />
            {/* Refreshing indicator pill */}
            <Fade in={isRefreshing} unmountOnExit>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "16px",
                  backgroundColor: "#E7F1FF",
                  border: `1px solid ${THEME.primary}33`
                }}
              >
                <ArrowClockwise16Regular
                  style={{
                    color: THEME.primary,
                    animation: "ctl-spin 0.8s linear infinite"
                  }}
                />
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.primary }}>
                  Updating results…
                </Typography>
              </Box>
            </Fade>
          </Box>

          {/* Right side - Date Range Toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarLtr24Regular style={{ color: THEME.primary }} />
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#323130" }}>
                Time Range:
              </Typography>
            </Box>
            <Tooltip
              title={
                dateRange === "custom"
                  ? `Showing custom range: ${customDateLabel}`
                  : dateRange === "2years"
                    ? `Showing last 2 years. Click "All Data" to view ${totalDataYears > 2 ? `all ${totalDataYears} years` : "all available data"}.`
                    : `Showing all ${totalDataYears} years of data. Click "Last 2 Years" to filter.`
              }
              arrow
              placement="top"
            >
              <ToggleButtonGroup
                value={dateRange}
                exclusive
                onChange={handleDateRangeChange}
                aria-label="Date range filter"
                size="small"
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  border: "1px solid #E1E5EB",
                  overflow: "hidden",
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: 0,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    px: 2,
                    py: 0.75,
                    color: "#605E5C",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#F3F2F1"
                    },
                    "&.Mui-selected": {
                      backgroundColor: THEME.primary,
                      color: "#FFFFFF",
                      "&:hover": {
                        backgroundColor: THEME.primaryDark
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="2years" aria-label="Last 2 years">
                  <History24Regular style={{ marginRight: 6, fontSize: 18 }} />
                  Last 2 Years
                </ToggleButton>
                <ToggleButton value="all" aria-label="All data">
                  <CalendarAgenda24Regular style={{ marginRight: 6, fontSize: 18 }} />
                  All Data
                  {totalDataYears > 2 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        px: 0.75,
                        py: 0.25,
                        fontSize: "11px",
                        fontWeight: 700,
                        backgroundColor: dateRange === "all" ? "rgba(255,255,255,0.2)" : THEME.warning,
                        color: dateRange === "all" ? "#FFFFFF" : "#323130",
                        borderRadius: "4px"
                      }}
                    >
                      {totalDataYears}y
                    </Box>
                  )}
                </ToggleButton>
                <ToggleButton value="custom" aria-label="Custom date range">
                  <CalendarEdit24Regular style={{ marginRight: 6, fontSize: 18 }} />
                  {dateRange === "custom" ? customDateLabel : "Custom"}
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>

            {/* Custom Date Range Popover */}
            <Popover
              open={Boolean(datePickerAnchor)}
              anchorEl={datePickerAnchor}
              onClose={handleDatePickerClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
              sx={{
                "& .MuiPaper-root": {
                  mt: 1,
                  borderRadius: "12px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                  border: "1px solid #E1E5EB"
                }
              }}
            >
              <Box sx={{ p: 2.5, minWidth: 320 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: THEME.textPrimary, mb: 2 }}>
                  Select Custom Date Range
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.textSecondary, mb: 0.5 }}>
                      Start Date
                    </Typography>
                    <DatePicker
                      placeholder="Select start date"
                      value={customDateRange.startDate || undefined}
                      onSelectDate={(date) => setCustomDateRange((prev: CustomDateRange) => ({ ...prev, startDate: date || null }))}
                      maxDate={customDateRange.endDate || new Date()}
                      styles={{
                        root: { width: "100%" },
                        textField: { width: "100%" }
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: THEME.textSecondary, mb: 0.5 }}>
                      End Date
                    </Typography>
                    <DatePicker
                      placeholder="Select end date"
                      value={customDateRange.endDate || undefined}
                      onSelectDate={(date) => setCustomDateRange((prev: CustomDateRange) => ({ ...prev, endDate: date || null }))}
                      minDate={customDateRange.startDate || undefined}
                      maxDate={new Date()}
                      styles={{
                        root: { width: "100%" },
                        textField: { width: "100%" }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between", mt: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleCustomDateClear}
                      disabled={!customDateRange.startDate && !customDateRange.endDate && dateRange !== "custom"}
                      sx={{
                        textTransform: "none",
                        color: THEME.error,
                        "&:hover": {
                          backgroundColor: "rgba(211, 52, 56, 0.08)"
                        },
                        "&.Mui-disabled": {
                          color: "#A19F9D"
                        }
                      }}
                    >
                      Clear
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleDatePickerClose}
                        sx={{
                          textTransform: "none",
                          borderColor: "#E1E5EB",
                          color: THEME.textSecondary,
                          "&:hover": {
                            borderColor: THEME.primary,
                            backgroundColor: "transparent"
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleCustomDateApply}
                        disabled={!customDateRange.startDate}
                        sx={{
                          textTransform: "none",
                          backgroundColor: THEME.primary,
                          "&:hover": {
                            backgroundColor: THEME.primaryDark
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#E1E5EB",
                            color: "#A19F9D"
                          }
                        }}
                      >
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Popover>
          </Box>
        </Box>

        {/* Content sections - subtly dimmed while refreshing so the change is noticeable */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            transition: "opacity 0.25s ease, filter 0.25s ease",
            opacity: isRefreshing ? 0.55 : 1,
            filter: isRefreshing ? "grayscale(0.15)" : "none",
            pointerEvents: isRefreshing ? "none" : "auto"
          }}
        >
          {/* Top Section with Executive Summary */}
          <TopSection />

          {/* Monthly Insights Charts */}
          <MonthlyInsights />

          {/* Top Themes Section */}
          <TopThemes />

          {/* Pipeline Snapshot and Breakdown Charts */}
          <BreakdownCharts />

          {/* Geography and Priority Donut Charts */}
          <CombinedDonutCharts />

          {/* SLA Performance */}
          <SLAPerformance />
        </Box>
      </Stack>
    </OverviewContext.Provider>
  );
};

export default Overview;
